import { IconArrowRight, IconMessage2 } from "@tabler/icons-react";
import { FooterLinkList } from "./FooterLinkList";
import { useLocale, useTranslations } from "../helpers/translations";
import { generateLocalizedPath } from "./nav/helpers/links";
import {
  EXTERNAL_ROUTES,
  MARKETING_ROUTES,
  RAILS_ROUTES,
} from "./nav/helpers/routes";
import { Button } from "@/components/ui/button";
import React from "react";
import { RegionLocaleSelector } from "./nav/utility-links/user-menu/RegionLocaleSelector";

export const Footer = ({
  contactPhoneNumber,
}: {
  contactPhoneNumber: string;
}) => {
  const t = useTranslations("footer");
  const locale = useLocale();
  const tPlatform = useTranslations("nav.platform");
  const tApplication = useTranslations("nav.application");
  const tResources = useTranslations("nav.resources");
  const tCommunity = useTranslations("nav.community");

  const platformLinks = {
    title: tPlatform("title"),
    links: [
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.MANUFACTURING_AUTOMATION_PLATFORM,
          locale,
        ),
        label: t("platform.links.vention_platform"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.MACHINE_BUILDER, locale),
        label: tPlatform("col1.links.machine_builder.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.MACHINE_LOGIC, locale),
        label: tPlatform("col1.links.machine_logic.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.MACHINE_ANALYTICS, locale),
        label: tPlatform("col1.links.machine_analytics.title"),
      },
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.SERVICES_REMOTE_SUPPORT,
          locale,
        ),
        label: tPlatform("col1.links.remote_support.title"),
      },
    ],
  };

  const hardwareTechnologyLinks = {
    title: t("hardware_technology.title"),
    links: [
      {
        href: generateLocalizedPath(RAILS_ROUTES.HARDWARE, locale),
        label: tPlatform("col2.links.extrusions.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.ACTUATORS, locale),
        label: tPlatform("col2.links.actuators.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.CONVEYORS, locale),
        label: tPlatform("col2.links.conveyors.title"),
      },
      {
        href: generateLocalizedPath(MARKETING_ROUTES.ROBOT_ARM, locale),
        label: tPlatform("col2.links.robot_arms.title"),
      },
    ],
  };

  const artificialIntelligenceLinks = {
    title: t("artificial_intelligence.title"),
    links: [
      {
        href: generateLocalizedPath(
          MARKETING_ROUTES.PHYSICAL_AI_PIPELINE,
          locale,
        ),
        label: tPlatform("col3.links.griip_physical_ai.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.MACHINEMOTION_AI, locale),
        label: tPlatform("col3.links.machine_motion_ai.title"),
      },
    ],
  };

  const applicationLinks = {
    title: tApplication("legacy_title"),
    links: [
      {
        href: generateLocalizedPath(RAILS_ROUTES.APPLICATIONS, locale),
        label: t("applications.links.all_applications"),
      },
      {
        href: generateLocalizedPath(MARKETING_ROUTES.RAPID_OPERATOR_AI, locale),
        label: tApplication("col1.aiOperator.title"),
      },
      {
        href: generateLocalizedPath(MARKETING_ROUTES.COBOT_PALLETIZER, locale),
        label: tApplication("col1.palletizing.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.MACHINE_TENDING, locale),
        label: tApplication("col2.tending.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.ROBOTIC_WELDING, locale),
        label: tApplication("col2.welding.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.INDUSTRIAL_CARTS, locale),
        label: tApplication("col3.industrial_carts.title"),
      },
    ],
  };

  const industriesLinks = {
    title: tApplication("industries.header"),
    links: [
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.INDUSTRIES_AUTOMOTIVE,
          locale,
        ),
        label: tApplication("industries.links.automotive.title"),
      },
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.INDUSTRIES_AEROSPACE_DEFENSE,
          locale,
        ),
        label: tApplication("industries.links.aerospace.title"),
      },
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.INDUSTRIES_CONSUMER_GOODS,
          locale,
        ),
        label: tApplication("industries.links.consumer_goods.title"),
      },
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.INDUSTRIES_EDUCATION_RESEARCH,
          locale,
        ),
        label: tApplication("industries.links.education.title"),
      },
    ],
  };

  const designLibraryLinks = {
    title: t("design_library.title"),
    links: [
      {
        href: generateLocalizedPath(RAILS_ROUTES.DESIGNS_OVERVIEW, locale),
        label: t("design_library.links.browse_all_designs"),
      },
    ],
  };

  const resourcesLinks = {
    title: tResources("title"),
    links: [
      {
        href: EXTERNAL_ROUTES.DOC360_TECH_DOC_KNOWLEDGE_BASE,
        label: t("resources.links.resource_center"),
      },
      {
        href: generateLocalizedPath(MARKETING_ROUTES.SERVICES, locale),
        label: t("resources.links.service_and_support"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.CUSTOMER_STORIES, locale),
        label: tResources("get_inspired.links.customer_stories.title"),
      },
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.RESOURCES_VIDEO_TUTORIALS,
          locale,
        ),
        label: tResources("col1.links.video_tutorials.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.WEBINARS, locale),
        label: tCommunity("col1.links.webinars.title"),
      },
      {
        href: EXTERNAL_ROUTES.DOC360_TECH_DOC_KNOWLEDGE_BASE,
        label: t("resources.links.documentation"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.BLOGS, locale),
        label: tResources("get_inspired.links.blog.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.TOOLS, locale),
        label: t("tools.links.browse_tools"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_FAQS, locale),
        label: t("resources.links.faq"),
      },
    ],
  };

  const servicesLinks = {
    title: tApplication("services.header"),
    links: [
      {
        href: generateLocalizedPath(MARKETING_ROUTES.SERVICES, locale),
        label: tApplication("services.links.service_packages.title"),
      },
      {
        href: generateLocalizedPath(
          MARKETING_ROUTES.SERVICES_DESIGN_SERVICES,
          locale,
        ),
        label: tApplication("services.links.design_services.title"),
      },
      {
        href: generateLocalizedPath(
          MARKETING_ROUTES.SERVICES_DEPLOYMENT_SERVICES,
          locale,
        ),
        label: tApplication("services.links.deployment_assistance.title"),
      },
      {
        href: generateLocalizedPath(
          MARKETING_ROUTES.SERVICES_REMOTE_SUPPORT,
          locale,
        ),
        label: tApplication("services.links.post_sale_support.title"),
      },
      {
        href: generateLocalizedPath(
          MARKETING_ROUTES.SERVICES_TRAINING_SESSIONS,
          locale,
        ),
        label: tApplication("services.links.professional_services.title"),
      },
    ],
  };

  const connectLinks = {
    title: t("connect.title"),
    links: [
      {
        href: generateLocalizedPath(RAILS_ROUTES.ABOUT, locale),
        label: t("connect.links.about_us"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.PRESS, locale),
        label: tCommunity("in_the_news.links.press.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.EVENTS, locale),
        label: tCommunity("in_the_news.links.events.title"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.CAREERS, locale),
        label: tCommunity("vention.links.careers.title"),
      },
      {
        href: generateLocalizedPath(
          RAILS_ROUTES.VENTION_AUTOMATION_PARTNER,
          locale,
        ),
        label: t("connect.links.become_automation_partner"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.MARKETPLACE_PROGRAM, locale),
        label: t("connect.links.marketplace_program"),
      },
      {
        href: generateLocalizedPath(RAILS_ROUTES.CONTACT, locale),
        label: t("connect.links.contact_us"),
      },
    ],
  };

  return (
    <div className="dark relative z-[1] bg-gray-light-800 shadow-top-sm">
      <div className="container py-6xl text-xs-regular text-primary-on-brand space-y-7xl sm:flex sm:justify-between sm:space-y-0">
        <div className="space-y-4xl col-span-2 grow max-w-xs sm:mr-7xl">
          <div>
            <a href={"/"} className="block mb-3xl max-w-11xl">
              <img
                src="https://assets.vention.io/marketing/page-assets/Footer/Footer_Vention_Logo.svg"
                alt="Vention"
                className="h-3xl w-auto"
              />
            </a>
            <div className="text-md-medium">{t("sub_logo_text")}</div>
          </div>
          <div>
            <RegionLocaleSelector
              labelClassName="text-sm-semibold text-secondary-on-brand"
              triggerClassName="h-auto px-lg py-sm text-sm-semibold text-button-secondary-gray-fg bg-transparent hover:bg-transparent max-w-[200px] border-button-secondary-gray-border gap-xs [&>svg]:text-button-secondary-gray-fg"
              containerClassName="mb-xl"
            />
          </div>
          <div>
            <div className="mb-md text-sm-semibold text-secondary-on-brand">
              {t("contact")}
            </div>
            <div className="mb-md text-xs-regular">
              {t("email")}
              <a
                href="mailto:info@vention.cc"
                className="no-underline text-primary-on-brand ml-xs hover:text-secondary-on-brand hover:no-underline"
              >
                info@vention.cc
              </a>
            </div>
            <div className="text-xs-regular">
              {t("call")}
              <a
                href={`tel:${contactPhoneNumber}`}
                className="no-underline text-primary-on-brand ml-xs hover:text-secondary-on-brand hover:underline"
              >
                {contactPhoneNumber}
              </a>
            </div>
          </div>
          <div>
            <div className="mb-xl">
              <a
                href={generateLocalizedPath(RAILS_ROUTES.CONTACT_NEW, locale)}
                className="block"
              >
                <Button
                  size="xl"
                  className="bg-button-primary-bg w-full text-md-semibold"
                >
                  {t("book_a_demo")}
                  <IconArrowRight className="ml-md" size={20} />
                </Button>
              </a>
            </div>
            <div>
              <a
                href={generateLocalizedPath(RAILS_ROUTES.CONTACT, locale)}
                className="block"
              >
                <Button
                  size="xl"
                  variant={"secondaryGray"}
                  className="w-full text-md-semibold"
                >
                  {t("support_button")}
                  <IconMessage2 className="ml-md" size={20} />
                </Button>
              </a>
            </div>
          </div>
          <div>
            <div className="flex mb-md">
              <img
                src="https://assets.vention.io/svg/icons/shield-check.svg"
                alt="Security shield"
                className="mr-xs"
              />
              {t("compliance")}
            </div>
            <p>ISO 27001 & NIST</p>
          </div>
        </div>
        <div className="grow lg:flex lg:justify-between grid grid-cols-2 gap-y-7xl gap-x-4xl lg:gap-y-0 lg:gap-x-0">
          <div className="text-secondary-on-brand">
            <FooterLinkList {...platformLinks} />
            <FooterLinkList {...hardwareTechnologyLinks} />
            <FooterLinkList {...artificialIntelligenceLinks} />
          </div>
          <div className="text-secondary-on-brand">
            <FooterLinkList {...applicationLinks} />
            <FooterLinkList {...industriesLinks} />
            <FooterLinkList {...designLibraryLinks} />
          </div>
          <div className="text-secondary-on-brand">
            <FooterLinkList {...resourcesLinks} />
          </div>
          <div className="text-secondary-on-brand">
            <FooterLinkList {...servicesLinks} />
            <FooterLinkList {...connectLinks} />
          </div>
        </div>
      </div>
    </div>
  );
};
